import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';

const LOGO_MAIN = "https://customer-assets.emergentagent.com/job_powerline-booking/artifacts/a0kugkor_IslandCoastalElectric-.V2-VARIANT.png";
const LOGO_ICON = "https://customer-assets.emergentagent.com/job_powerline-booking/artifacts/co7mv4rs_IslandCoastalElectric-MountainOnly-Logo.V3-VECTOR.png";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    // Scroll to top on route change
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'About', path: '/about' },
    { name: 'Areas We Serve', path: '/areas' },
    { name: 'Blog', path: '/blog' },
    { name: 'Real Results', path: '/gallery' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Contact', path: '/contact' }
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header
      data-testid="main-header"
      className="fixed top-0 left-0 right-0 z-50"
    >
      {/* Top Bar - Logo, Contact Info, Book Button */}
      <div className={`transition-all duration-300 ${isScrolled ? 'bg-primary-dark/95 backdrop-blur-md' : 'bg-primary-dark'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-3 md:py-4">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
              <img 
                src={LOGO_ICON} 
                alt="Island Coastal Electric" 
                className="h-10 sm:h-12 md:h-14 w-auto"
              />
              <div className="hidden xs:block">
                <span className="text-white font-black text-base sm:text-lg md:text-xl tracking-tight block leading-tight">ISLAND COASTAL</span>
                <span className="text-electric-teal text-xs sm:text-sm md:text-base font-bold tracking-widest">ELECTRIC</span>
              </div>
            </Link>

            {/* Contact Info - Hidden on small mobile */}
            <div className="hidden md:flex items-center gap-6 lg:gap-8">
              <a 
                href="tel:+17789602377" 
                className="flex items-center gap-2 text-white/80 hover:text-electric-teal transition-colors"
                data-testid="phone-link"
              >
                <Phone className="w-4 h-4 lg:w-5 lg:h-5 text-electric-teal" />
                <span className="text-sm lg:text-base font-semibold">778.960.2377</span>
              </a>
              <a 
                href="mailto:info@islandcoastalelectric.ca" 
                className="flex items-center gap-2 text-white/80 hover:text-electric-teal transition-colors"
                data-testid="email-link"
              >
                <Mail className="w-4 h-4 lg:w-5 lg:h-5 text-electric-teal" />
                <span className="text-sm lg:text-base font-semibold">info@islandcoastalelectric.ca</span>
              </a>
            </div>

            {/* Book Estimate Button */}
            <div className="flex items-center gap-3">
              <Link to="/book-estimate" className="hidden sm:block" data-testid="book-estimate-btn">
                <Button className="bg-electric-teal text-primary-dark font-bold uppercase tracking-wider text-xs sm:text-sm px-4 sm:px-6 py-2 sm:py-2.5 hover:bg-white transition-all duration-300 btn-glow">
                  <Zap className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
                  Book Estimate
                </Button>
              </Link>
              
              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2 text-white hover:text-electric-teal transition-colors"
                data-testid="mobile-menu-btn"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Bar */}
      <nav className={`hidden lg:block transition-all duration-300 border-t border-white/10 ${
        isScrolled ? 'bg-secondary-dark/95 backdrop-blur-md' : 'bg-secondary-dark'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-8 xl:gap-12 py-3">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
                className={`text-sm font-bold uppercase tracking-widest transition-colors relative py-1 ${
                  isActive(link.path)
                    ? 'text-electric-teal'
                    : 'text-white/80 hover:text-electric-teal'
                }`}
              >
                {link.name}
                {isActive(link.path) && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-electric-teal rounded-full" />
                )}
              </Link>
            ))}
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-secondary-dark border-t border-white/10"
            data-testid="mobile-menu"
          >
            <div className="max-w-7xl mx-auto px-4 py-4 space-y-3">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-${link.name.toLowerCase().replace(' ', '-')}`}
                  className={`block text-base font-bold uppercase tracking-wider py-2 px-3 rounded-lg transition-all ${
                    isActive(link.path) 
                      ? 'text-electric-teal bg-electric-teal/10' 
                      : 'text-white/80 hover:bg-white/5'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              
              {/* Mobile Contact Info */}
              <div className="pt-4 mt-4 border-t border-white/10 space-y-3">
                <a 
                  href="tel:+17789602377" 
                  className="flex items-center gap-3 text-white/80 hover:text-electric-teal py-2 px-3"
                  data-testid="mobile-phone-link"
                >
                  <Phone className="w-5 h-5 text-electric-teal" />
                  <span className="font-semibold">778.960.2377</span>
                </a>
                <a 
                  href="mailto:info@islandcoastalelectric.ca" 
                  className="flex items-center gap-3 text-white/80 hover:text-electric-teal py-2 px-3"
                  data-testid="mobile-email-link"
                >
                  <Mail className="w-5 h-5 text-electric-teal" />
                  <span className="font-semibold">info@islandcoastalelectric.ca</span>
                </a>
                <Link to="/book-estimate" className="block pt-2" data-testid="mobile-book-btn">
                  <Button className="w-full bg-electric-teal text-primary-dark font-bold uppercase tracking-wider py-3">
                    <Zap className="w-4 h-4 mr-2" />
                    Book Estimate
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
